import { t } from '../i18n';
import { travellerName } from './traveller';
import type { Route, RouteDestination } from '../backend/route';

export type HeroCopy = {
  /** What dominates the eye — title, destination sentence, or default. */
  headline: string;
  /** Optional second line — only shown when the headline isn't already the sentence. */
  subtitle: string | null;
  /** Screen-reader announcement (aria-live=polite). */
  announcement: string;
};

/**
 * Hero copy hierarchy — see docs/WATCHER_REBUILD.md F3.
 *
 * Brand tone rule: never assume mode of transport, never assume destination.
 * Default sentence is "{name} is on the way". Extend only when data justifies it.
 *
 * Transportation never enters the sentence (avoids a translation explosion
 * across 11 modes × destination-or-not × locales). It surfaces as a chip
 * next to the hero, rendered by TransportChip.
 */
export function buildHeroCopy(route: Route | null, finishedAtFormatted: string | null): HeroCopy {
  const name = displayName(route);
  if (!route) {
    return {
      headline: t('status.onTheWay', { name }),
      subtitle: null,
      announcement: t('ariaLive.travelling', { name }),
    };
  }
  if (route.finished) {
    if (finishedAtFormatted) {
      const arrived = t('status.arrived', { name, time: finishedAtFormatted });
      return { headline: arrived, subtitle: null, announcement: t('ariaLive.arrived', { name }) };
    }
    const arrived = t('status.arrivedNoTime', { name });
    return { headline: arrived, subtitle: null, announcement: t('ariaLive.arrived', { name }) };
  }
  if (route.title && route.title.trim()) {
    return {
      headline: route.title.trim(),
      subtitle: t('status.titleSubtitle', { name }),
      announcement: t('ariaLive.travelling', { name }),
    };
  }
  if (route.destination) {
    return {
      headline: t('status.onTheWayTo', { name, destination: destinationLabel(route.destination) }),
      subtitle: null,
      announcement: t('ariaLive.travelling', { name }),
    };
  }
  return {
    headline: t('status.onTheWay', { name }),
    subtitle: null,
    announcement: t('ariaLive.travelling', { name }),
  };
}

function displayName(route: Route | null): string {
  // In-page copy can carry a placeholder; share previews can't, which is why
  // the shared helper returns null and each caller picks its own fallback.
  return travellerName(route) ?? '—';
}

function destinationLabel(destination: RouteDestination): string {
  return (destination.name?.trim() || destination.address.trim()) || '—';
}
